import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, User, Lock, CreditCard, Trash2, Crown, AlertTriangle, ChevronDown, Globe, FileDown, MapPin, Users } from 'lucide-react';
import { authApi } from '../api/auth';
import { billingApi } from '../api/billing';
import { certificatesApi } from '../api/certificates';
import { useAuthStore } from '../store/authStore';
import ReferralCodeInput from '../components/ReferralCodeInput';
import ReferralCodeApplied from '../components/ReferralCodeApplied';

export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { user, fetchCurrentUser } = useAuthStore();

  // Form states
  const [username, setUsername] = useState(user?.username || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');

  // Venue info states
  const [venueName, setVenueName] = useState(user?.profile?.venueName || '');
  const [venueAddress, setVenueAddress] = useState(user?.profile?.venueAddress || '');

  // Correspondence language state
  const [correspondenceLanguage, setCorrespondenceLanguage] = useState(user?.correspondenceLanguage || 'en');
  const [savingLanguage, setSavingLanguage] = useState(false);

  // Loading states
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingVenue, setSavingVenue] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [cancellingDeletion, setCancellingDeletion] = useState(false);
  const [downloadingCertificate, setDownloadingCertificate] = useState(false);

  // UI states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [venueMessage, setVenueMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Subscription status - always refetch on mount (e.g. when returning from Stripe Portal)
  const { data: status } = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: billingApi.getStatus,
    refetchOnMount: 'always',
  });

  // Refetch user data when page loads (e.g. after returning from Stripe Portal)
  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  // Scroll to section if hash present
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location.hash]);

  // Update username when user changes
  useEffect(() => {
    if (user?.username) {
      setUsername(user.username);
    }
  }, [user?.username]);

  // Update correspondence language when user changes
  useEffect(() => {
    if (user?.correspondenceLanguage) {
      setCorrespondenceLanguage(user.correspondenceLanguage);
    }
  }, [user?.correspondenceLanguage]);

  // Update venue info when user changes
  useEffect(() => {
    if (user?.profile?.venueName !== undefined) {
      setVenueName(user.profile.venueName || '');
    }
    if (user?.profile?.venueAddress !== undefined) {
      setVenueAddress(user.profile.venueAddress || '');
    }
  }, [user?.profile?.venueName, user?.profile?.venueAddress]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setProfileMessage(null);
    try {
      await authApi.updateProfile({ username: username || undefined });
      await fetchCurrentUser();
      setProfileMessage({ type: 'success', text: t('profile.saved', 'Profile saved successfully') });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to save profile';
      setProfileMessage({ type: 'error', text: message });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveVenue = async () => {
    setSavingVenue(true);
    setVenueMessage(null);
    try {
      await authApi.updateProfile({
        profile: {
          venueName: venueName || undefined,
          venueAddress: venueAddress || undefined,
        },
      });
      await fetchCurrentUser();
      setVenueMessage({ type: 'success', text: t('profile.venueSaved', 'Venue information saved') });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to save venue info';
      setVenueMessage({ type: 'error', text: message });
    } finally {
      setSavingVenue(false);
    }
  };

  const handleLanguageChange = async (newLanguage: string) => {
    setCorrespondenceLanguage(newLanguage);
    setSavingLanguage(true);
    try {
      await authApi.updateCorrespondenceLanguage(newLanguage);
      await fetchCurrentUser();
    } catch (error) {
      // Revert on error
      setCorrespondenceLanguage(user?.correspondenceLanguage || 'en');
      console.error('Failed to update language:', error);
    } finally {
      setSavingLanguage(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) return;

    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: t('profile.passwordMismatch', 'Passwords do not match') });
      return;
    }

    setChangingPassword(true);
    setPasswordMessage(null);
    try {
      await authApi.changePassword({ currentPassword, newPassword, confirmPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordMessage({ type: 'success', text: t('profile.passwordChanged', 'Password changed successfully') });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to change password';
      setPasswordMessage({ type: 'error', text: message });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) return;

    setDeletingAccount(true);
    try {
      await authApi.deleteAccount(deletePassword);
      await fetchCurrentUser();
      setShowDeleteConfirm(false);
      setDeletePassword('');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete account');
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleCancelDeletion = async () => {
    setCancellingDeletion(true);
    try {
      await authApi.cancelDeletion();
      await fetchCurrentUser();
    } catch (error) {
      console.error('Failed to cancel deletion:', error);
    } finally {
      setCancellingDeletion(false);
    }
  };

  const handleManageSubscription = async () => {
    setOpeningPortal(true);
    try {
      const { url } = await billingApi.createPortal(i18n.language);
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Failed to open portal:', error);
    } finally {
      setOpeningPortal(false);
    }
  };

  const handleDownloadCertificate = async () => {
    setDownloadingCertificate(true);
    try {
      const blob = await certificatesApi.downloadCertificate(i18n.language);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fonorex_license_certificate.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download certificate:', error);
    } finally {
      setDownloadingCertificate(false);
    }
  };

  const isAccountScheduledForDeletion = !!user?.deleteScheduledAt;

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/home"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          {t('profile.back', 'Back')}
        </Link>
        <h1 className="text-3xl font-bold text-white">{t('profile.title', 'Profile')}</h1>
      </div>

      {/* Account scheduled for deletion warning */}
      {isAccountScheduledForDeletion && (
        <div className="mb-8 p-4 bg-red-500/20 border border-red-500/50 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-red-400 font-semibold mb-1">
                {t('profile.deletionScheduled', 'Account deletion scheduled')}
              </h3>
              <p className="text-gray-300 text-sm mb-3">
                {t('profile.deletionDate', 'Your account will be deleted on')}{' '}
                {user?.deletedAt && new Date(user.deletedAt).toLocaleDateString()}
              </p>
              <button
                onClick={handleCancelDeletion}
                disabled={cancellingDeletion}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-all disabled:opacity-50"
              >
                {cancellingDeletion
                  ? t('profile.cancelling', 'Cancelling...')
                  : t('profile.cancelDeletion', 'Cancel deletion')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Account Info Section */}
      <section className="mb-8 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary-500/20 rounded-lg flex items-center justify-center">
            <User className="w-5 h-5 text-primary-400" />
          </div>
          <h2 className="text-xl font-semibold text-white">{t('profile.account', 'Account')}</h2>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              {t('profile.email', 'Email')}
            </label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-gray-400 cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              {t('profile.username', 'Username')}
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('profile.usernamePlaceholder', 'Enter username')}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 transition-colors"
            />
          </div>

          {profileMessage && (
            <p className={`text-sm ${profileMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
              {profileMessage.text}
            </p>
          )}

          <button
            onClick={handleSaveProfile}
            disabled={savingProfile || username === (user?.username || '')}
            className={`px-6 py-2 text-white font-medium rounded-lg transition-all disabled:opacity-50 ${
              username === (user?.username || '')
                ? 'bg-primary-500/50 cursor-not-allowed'
                : 'bg-primary-500 hover:bg-primary-600'
            }`}
          >
            {savingProfile ? t('profile.saving', 'Saving...') : t('profile.save', 'Save')}
          </button>
        </div>
      </section>

      {/* Correspondence Language Section */}
      <section className="mb-8 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
            <Globe className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white">{t('profile.correspondenceLanguage', 'Correspondence language')}</h2>
            <p className="text-sm text-gray-400">{t('profile.correspondenceLanguageDescription', 'Language for emails we send you')}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {[
            { code: 'en', name: 'English', flag: '🇬🇧' },
            { code: 'es', name: 'Español', flag: '🇪🇸' },
            { code: 'lt', name: 'Lietuvių', flag: '🇱🇹' },
            { code: 'ru', name: 'Русский', flag: '🇷🇺' },
          ].map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              disabled={savingLanguage}
              className={`px-4 py-2 rounded-lg font-medium transition-all disabled:opacity-50 flex items-center gap-2 ${
                correspondenceLanguage === lang.code
                  ? 'bg-blue-500 text-white'
                  : 'bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10'
              }`}
            >
              <span>{lang.flag}</span>
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Subscription Section */}
      <section id="subscription" className="mb-8 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 scroll-mt-24">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-yellow-400" />
          </div>
          <h2 className="text-xl font-semibold text-white">{t('profile.subscription', 'Subscription')}</h2>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-gray-400">{t('profile.status', 'Status')}:</span>
            {status?.status === 'active' && (
              <span className="flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">
                <Crown size={14} />
                {t('profile.active', 'Active')}
              </span>
            )}
            {status?.status === 'trialing' && (
              <span className="flex items-center gap-2 px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-medium">
                {t('profile.trial', 'Trial')}
              </span>
            )}
            {(!status?.status || status?.status === 'canceled') && (
              <span className="px-3 py-1 bg-gray-500/20 text-gray-400 rounded-full text-sm font-medium">
                {t('profile.noSubscription', 'No subscription')}
              </span>
            )}
          </div>

          {status?.status === 'trialing' && status.trialEndsAt && (
            <p className="text-gray-400 text-sm">
              {t('profile.trialEnds', 'Trial ends on')}{' '}
              {new Date(status.trialEndsAt).toLocaleDateString()}
            </p>
          )}

          {status?.status === 'active' && status.currentPeriodEnd && (
            <p className="text-gray-400 text-sm">
              {status.cancelAtPeriodEnd
                ? t('profile.expiresOn', 'Expires on')
                : t('profile.renewsOn', 'Renews on')}{' '}
              {new Date(status.currentPeriodEnd).toLocaleDateString()}
            </p>
          )}

          {status?.status === 'active' ? (
            <button
              onClick={handleManageSubscription}
              disabled={openingPortal}
              className="px-6 py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-medium rounded-lg transition-all disabled:opacity-50"
            >
              {openingPortal
                ? t('profile.opening', 'Opening...')
                : t('profile.manageSubscription', 'Manage subscription')}
            </button>
          ) : (
            <Link
              to="/pricing"
              className="inline-block px-6 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-medium rounded-lg transition-all"
            >
              {status?.status === 'trialing'
                ? t('profile.choosePlan', 'Choose a plan')
                : t('profile.subscribe', 'Subscribe')}
            </Link>
          )}
        </div>
      </section>

      {/* Venue Information Section - Only for subscribers */}
      {(status?.status === 'active' || status?.status === 'trialing') && (
        <section className="mb-8 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">{t('profile.venueInfo', 'Venue Information')}</h2>
              <p className="text-sm text-gray-400">{t('profile.venueInfoDescription', 'This information will appear on your license certificate')}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                {t('profile.venueName', 'Venue Name')}
              </label>
              <input
                type="text"
                value={venueName}
                onChange={(e) => setVenueName(e.target.value)}
                placeholder={t('profile.venueNamePlaceholder', 'e.g. Blue Wave Restaurant')}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                {t('profile.venueAddress', 'Venue Address')}
              </label>
              <input
                type="text"
                value={venueAddress}
                onChange={(e) => setVenueAddress(e.target.value)}
                placeholder={t('profile.venueAddressPlaceholder', 'e.g. Calle Mayor 15, Madrid')}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 transition-colors"
              />
            </div>

            {venueMessage && (
              <p className={`text-sm ${venueMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                {venueMessage.text}
              </p>
            )}

            {/* Show hint if venue info is incomplete */}
            {(!venueName || !venueAddress) ? (
              <p className="text-sm text-yellow-400/80">
                {!venueName && !venueAddress
                  ? t('profile.venueHintBoth', 'Fill in venue name and address to download certificate')
                  : !venueName
                    ? t('profile.venueHintName', 'Fill in venue name to download certificate')
                    : t('profile.venueHintAddress', 'Fill in venue address to download certificate')}
              </p>
            ) : venueName !== (user?.profile?.venueName || '') || venueAddress !== (user?.profile?.venueAddress || '') ? (
              /* Show save button if there are changes */
              <button
                onClick={handleSaveVenue}
                disabled={savingVenue}
                className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-all disabled:opacity-50"
              >
                {savingVenue ? t('profile.saving', 'Saving...') : t('profile.saveVenue', 'Save venue info')}
              </button>
            ) : (
              /* Show download button if no changes */
              <button
                onClick={handleDownloadCertificate}
                disabled={downloadingCertificate}
                className="px-6 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/50 text-green-400 font-medium rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
              >
                <FileDown className="w-4 h-4" />
                {downloadingCertificate
                  ? t('certificate.downloading', 'Downloading...')
                  : t('certificate.downloadCertificate', 'Download license certificate')}
              </button>
            )}
          </div>
        </section>
      )}

      {/* Referral Code Section - show if no referral code yet and no active paid subscription */}
      {!user?.referredBy && user?.subscriptionStatus !== 'active' && (
        <section className="mb-8">
          <ReferralCodeInput variant="inline" />
        </section>
      )}

      {/* Show applied referral code if user has one but no active subscription yet */}
      {user?.referredBy && user?.subscriptionStatus !== 'active' && (
        <section className="mb-8">
          <ReferralCodeApplied code={user.referredBy} />
        </section>
      )}

      {/* Affiliate Program Section */}
      <section className="mb-8 bg-gradient-to-br from-orange-500/10 to-yellow-500/10 backdrop-blur-xl border border-orange-500/20 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
            <Users className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white">{t('profile.affiliateProgram', 'Affiliate Program')}</h2>
            <p className="text-sm text-gray-400">{t('profile.affiliateProgramDescription', 'Earn 10% recurring commission')}</p>
          </div>
        </div>

        <p className="text-gray-300 text-sm mb-4">
          {t('profile.affiliateProgramInfo', 'Refer businesses to Fonorex and earn 10% of every payment they make. Recurring commissions as long as they stay subscribed!')}
        </p>

        {user?.isAffiliate ? (
          <Link
            to="/affiliate/dashboard"
            className="inline-flex items-center gap-2 px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-all"
          >
            {t('profile.affiliateDashboard', 'Go to Affiliate Dashboard')}
          </Link>
        ) : (
          <Link
            to="/affiliate/apply"
            className="inline-flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-medium rounded-lg transition-all"
          >
            {t('profile.becomeAffiliate', 'Become an Affiliate')}
          </Link>
        )}
      </section>

      {/* Password Section - Collapsible */}
      <section className="mb-8 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl">
        <button
          onClick={() => setShowPasswordSection(!showPasswordSection)}
          className="w-full p-6 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-secondary-500/20 rounded-lg flex items-center justify-center">
              <Lock className="w-5 h-5 text-secondary-400" />
            </div>
            <h2 className="text-xl font-semibold text-white">{t('profile.changePassword', 'Change password')}</h2>
          </div>
          <ChevronDown
            className={`w-5 h-5 text-gray-400 transition-transform ${showPasswordSection ? 'rotate-180' : ''}`}
          />
        </button>

        {showPasswordSection && (
          <div className="px-6 pb-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                {t('profile.currentPassword', 'Current password')}
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                {t('profile.newPassword', 'New password')}
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                {t('profile.confirmPassword', 'Confirm password')}
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 transition-colors"
              />
            </div>

            {passwordMessage && (
              <p className={`text-sm ${passwordMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                {passwordMessage.text}
              </p>
            )}

            <button
              onClick={handleChangePassword}
              disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
              className="px-6 py-2 bg-secondary-500 hover:bg-secondary-600 text-white font-medium rounded-lg transition-all disabled:opacity-50"
            >
              {changingPassword
                ? t('profile.changing', 'Changing...')
                : t('profile.changePassword', 'Change password')}
            </button>
          </div>
        )}
      </section>

      {/* Danger Zone */}
      <section className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-red-400">{t('profile.dangerZone', 'Danger Zone')}</h2>
        </div>

        {!showDeleteConfirm ? (
          <div>
            <p className="text-gray-400 text-sm mb-4">
              {t('profile.deleteWarning', 'Once you delete your account, you have 30 days to change your mind. After that, all your data will be permanently removed.')}
            </p>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isAccountScheduledForDeletion}
              className="px-6 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 text-red-400 font-medium rounded-lg transition-all disabled:opacity-50"
            >
              {t('profile.deleteAccount', 'Delete account')}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-red-400 text-sm font-medium">
              {t('profile.confirmDelete', 'Enter your password to confirm deletion:')}
            </p>
            <input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder={t('profile.password', 'Password')}
              className="w-full px-4 py-3 bg-white/5 border border-red-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors"
            />
            <div className="flex gap-3">
              <button
                onClick={handleDeleteAccount}
                disabled={deletingAccount || !deletePassword}
                className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg transition-all disabled:opacity-50"
              >
                {deletingAccount
                  ? t('profile.deleting', 'Deleting...')
                  : t('profile.confirmDeleteBtn', 'Delete my account')}
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletePassword('');
                }}
                className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-all"
              >
                {t('profile.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
